'use client';

import { c as _c } from "react/compiler-runtime";
import './cart-item.scss';
import type { PropsWithChildren, ReactNode } from 'react';
import { ChildImage } from '@sola/ark-core/atoms/child-image/child-image.tsx';
import { Intl, IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { ChildImageVariantEnum } from '@sola/ark-core/utils/child-image.ts';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import type { ArkIconProps } from '@sola/ark-icons/lib/ark-icon.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { calcItemAmountWithAddOn, currentAddon } from '@sola/cart-sdk/addons.ts';
import { getRelatedItem } from '@sola/cart-sdk/cart.ts';
import { useCart } from '@sola/cart-sdk/store.tsx';
import type { CartItem as CartItemType } from '@sola/schema/dotcom/cart/cart-item.ts';
import { CartItemRelEnum, CartItemTypeEnum } from '@sola/schema/dotcom/cart/cart-item.ts';
import { CommitmentTypeEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import { SponsorshipItemTypeEnum } from '@sola/schema/dotcom/cart/sponsorship.ts';
import type { ChildCriteria } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import { GiftIconMap } from '@sola/shared-utils/cart/gifts.ts';
enum CriteriaKeyEnum {
  Gender = 'gender',
  Age = 'age',
  Country = 'countries',
  Region = 'region',
  Orphan = 'isOrphan',
  Disability = 'disability',
  HivAffected = 'isInHivAffectedArea',
}
export interface CartItemLabels {
  subtitle?: ReactNode;
  title?: ReactNode;
}
export interface CartItemProps extends PropsWithChildren {
  className?: string;
  hideThumbnail?: boolean;
  item: CartItemType;
}
const criteriaKeys = [CriteriaKeyEnum.Gender, CriteriaKeyEnum.Age, CriteriaKeyEnum.Country, CriteriaKeyEnum.Region, CriteriaKeyEnum.Orphan, CriteriaKeyEnum.Disability, CriteriaKeyEnum.HivAffected];
const showCriterionByKey: Record<CriteriaKeyEnum, (criteria: ChildCriteria) => boolean> = {
  [CriteriaKeyEnum.Gender]: criteria => criteria.gender != null,
  [CriteriaKeyEnum.Age]: criteria => criteria.ageMin != null || criteria.ageMax != null,
  [CriteriaKeyEnum.Country]: criteria => criteria.countries?.length === 1,
  [CriteriaKeyEnum.Region]: criteria => criteria.region != null && criteria.countries?.length !== 1,
  [CriteriaKeyEnum.Orphan]: criteria => criteria.isOrphan === true,
  [CriteriaKeyEnum.Disability]: criteria => criteria.disability === true,
  [CriteriaKeyEnum.HivAffected]: criteria => criteria.isInHivAffectedArea === true
};
const showCriterion = (key: CriteriaKeyEnum, criteria?: ChildCriteria) => {
  if (!criteria) {
    return false;
  }
  return showCriterionByKey[key](criteria);
};
const CartItemCriteria: React.FC<{
  item: CartItemType;
}> = t0 => {
  const $ = _c(4);
  const {
    item
  } = t0;
  if (item.itemType !== CartItemTypeEnum.Sponsorship || item.sponsorship.sponsorshipType !== SponsorshipItemTypeEnum.ChildCriteria || !item.sponsorship.criteria) {
    return null;
  }
  const {
    criteria
  } = item.sponsorship;
  let t1;
  if ($[0] !== criteria) {
    t1 = criteriaKeys.map(key => showCriterion(key, criteria) && <span className="CartItem__criterion flex gap-snug-md" key={key}><IntlBlock i18nKey="checkout:child.criteria.criterion" values={{
        context: key,
        ...criteria
      }} /></span>);
    $[0] = criteria;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="CartItem__criteria flex flex-col gap-snug-sm label-md">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const CartItemChildDetails: React.FC<{
  item: CartItemType;
}> = t0 => {
  const $ = _c(20);
  const {
    item
  } = t0;
  if (item.itemType !== CartItemTypeEnum.Sponsorship || item.sponsorship.sponsorshipType !== SponsorshipItemTypeEnum.Selected && item.sponsorship.sponsorshipType !== SponsorshipItemTypeEnum.FirstPayment && item.sponsorship.sponsorshipType !== SponsorshipItemTypeEnum.EPayment) {
    return null;
  }
  let t1;
  let t2;
  let t3;
  if ($[0] !== item.child) {
    const bdayDate = item.child?.birthDate ? new Date(item.child.birthDate) : null;
    t3 = "CartItem__criteria flex flex-col gap-snug-md label-md";
    t1 = "CartItem__criterion flex gap-snug-md items-center";
    t2 = bdayDate && <><ArkIcon iconName="birthday-cake" className="CartItem__criterion-icon" />{bdayDate.toLocaleDateString([], {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "utc"
      })}</>;
    $[0] = item.child;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t2) {
    t4 = <span className={t1}>{t2}</span>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== item.child) {
    t5 = item.child?.gender && <>{item.child.gender === ChildGenderEnum.Female ? <ArkIcon iconName="girl" className="CartItem__criterion-icon" /> : <ArkIcon iconName="boy" className="CartItem__criterion-icon" />}{item.child.gender}</>;
    $[7] = item.child;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <span className="CartItem__criterion flex gap-snug-md items-center">{t5}</span>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== item.child) {
    t7 = item.child?.country && <><ArkIcon iconName="marker-pin-01" className="CartItem__criterion-icon" />{item.child.country}</>;
    $[11] = item.child;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <span className="CartItem__criterion flex gap-snug-md items-center">{t7}</span>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t6 || $[18] !== t8) {
    t9 = <div className={t3}>{t4}{t6}{t8}</div>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t6;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
};
const CartItemIcon: React.FC<{
  iconName?: ArkIconProps['iconName'];
}> = t0 => {
  const $ = _c(4);
  const {
    iconName
  } = t0;
  let t1;
  if ($[0] !== iconName) {
    t1 = iconName && <ArkIcon iconName={iconName} />;
    $[0] = iconName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="CartItem__icon">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const CartItemImage: React.FC<{
  item: CartItemType;
}> = t0 => {
  const $ = _c(13);
  const {
    item
  } = t0;
  switch (item.itemType) {
    case CartItemTypeEnum.Sponsorship:
      {
        if (item.child?.imageUrl) {
          let t1;
          if ($[0] !== item.child.imageUrl || $[1] !== item.child.name) {
            t1 = <ChildImage variant={ChildImageVariantEnum.Circle} childImageUrl={item.child.imageUrl} name={item.child.name} width={56} />;
            $[0] = item.child.imageUrl;
            $[1] = item.child.name;
            $[2] = t1;
          } else {
            t1 = $[2];
          }
          return t1;
        }
        let t1;
        if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <CartItemIcon iconName="children" />;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
    case CartItemTypeEnum.Fund:
      {
        if (item.fund?.icon) {
          let t1;
          if ($[4] !== item.fund.icon) {
            t1 = <CartItemIcon iconName={item.fund.icon} />;
            $[4] = item.fund.icon;
            $[5] = t1;
          } else {
            t1 = $[5];
          }
          return t1;
        }
        let t1;
        if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <CartItemIcon iconName="heart-hand" />;
          $[6] = t1;
        } else {
          t1 = $[6];
        }
        return t1;
      }
    case CartItemTypeEnum.Payment:
      {
        if (item.child?.imageUrl && item.payment.commitmentType === CommitmentTypeEnum.ChildSponsorship) {
          let t1;
          if ($[7] !== item.child.imageUrl || $[8] !== item.child.name) {
            t1 = <ChildImage variant={ChildImageVariantEnum.Circle} childImageUrl={item.child.imageUrl} alt={item.child.name} width={56} />;
            $[7] = item.child.imageUrl;
            $[8] = item.child.name;
            $[9] = t1;
          } else {
            t1 = $[9];
          }
          return t1;
        }
        let t1;
        if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <CartItemIcon iconName="heart-hand" />;
          $[10] = t1;
        } else {
          t1 = $[10];
        }
        return t1;
      }
    case CartItemTypeEnum.Gift:
      {
        const t1 = GiftIconMap[item.gift.giftType];
        let t2;
        if ($[11] !== t1) {
          t2 = <CartItemIcon iconName={t1} />;
          $[11] = t1;
          $[12] = t2;
        } else {
          t2 = $[12];
        }
        return t2;
      }
  }
};
export const getCartItemLabels = (item: CartItemType): CartItemLabels => {
  switch (item.itemType) {
    case CartItemTypeEnum.Sponsorship:
      {
        switch (item.sponsorship.sponsorshipType) {
          case SponsorshipItemTypeEnum.Selected:
            {
              if (!item.child) {
                return {};
              }
              return {
                title: item.child.name,
                subtitle: <CartItemChildDetails item={item} />
              };
            }
          case SponsorshipItemTypeEnum.FirstPayment:
            {
              return {
                title: <Intl k="checkout:child.microcopy.firstDonation" variables={{
                  name: item.child?.name
                }} />,
                subtitle: <CartItemChildDetails item={item} />
              };
            }
          case SponsorshipItemTypeEnum.LongestWaiting:
            {
              return {
                title: <Intl k="checkout:child.microcopy.longestWaiting" />,
                subtitle: item.quantity === 1 ? <Intl k="checkout:child.microcopy.singleChildWaiting" /> : <Intl k="checkout:child.microcopy.multiChildrenWaiting" variables={{
                  qty: item.quantity
                }} />
              };
            }
          case SponsorshipItemTypeEnum.ChildCriteria:
            {
              return {
                title: item.quantity === 1 ? <Intl k="checkout:child.microcopy.singleChildSelected" /> : <Intl k="checkout:child.microcopy.multiChildrenSelected" variables={{
                  qty: item.quantity
                }} />,
                subtitle: <CartItemCriteria item={item} />
              };
            }
          case SponsorshipItemTypeEnum.EPayment:
            {
              return {
                title: item.child?.name,
                subtitle: <CartItemChildDetails item={item} />
              };
            }
        }
        break;
      }
    case CartItemTypeEnum.Payment:
      {
        if (item.payment.commitmentType === CommitmentTypeEnum.ChildSponsorship) {
          return {
            title: <Intl k="checkout:child.microcopy.commitmentPayment" variables={{
              name: item.child?.name
            }} />
          };
        }
        return {
          title: item.title
        };
      }
    case CartItemTypeEnum.Gift:
      {
        switch (item.gift.giftType) {
          case GiftTypeEnum.Birthday:
            {
              return {
                title: <Intl k="checkout:child.microcopy.giftBirthday" variables={{
                  name: item.child?.name
                }} />
              };
            }
          case GiftTypeEnum.Child:
            {
              return {
                title: <Intl k="checkout:child.microcopy.gift" variables={{
                  name: item.child?.name
                }} />
              };
            }
          case GiftTypeEnum.Family:
            {
              return {
                title: <Intl k="checkout:child.microcopy.giftFamily" variables={{
                  name: item.child?.name
                }} />,
                subtitle: <Intl k="checkout:child.microcopy.giftFamilyCta" variables={{
                  name: item.child?.name
                }} />
              };
            }
          case GiftTypeEnum.Center:
            {
              return {
                title: <Intl k="checkout:child.microcopy.giftCenter" variables={{
                  name: item.gift.isMergedCenterGift ? item.gift.centerId : item.child?.name
                }} />
              };
            }
        }
      }
  }
  return {
    title: item.title
  };
};
export const CartItem: React.FC<CartItemProps> = t0 => {
  const $ = _c(47);
  const {
    children,
    className,
    hideThumbnail,
    item
  } = t0;
  let t1;
  if ($[0] !== item) {
    t1 = getCartItemLabels(item);
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    title,
    subtitle
  } = t1;
  const {
    cart
  } = useCart("cart");
  let T0;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== cart || $[3] !== className || $[4] !== hideThumbnail || $[5] !== item || $[6] !== title) {
    const relatedAddOnInCart = getRelatedItem(item, CartItemRelEnum.AddOn, cart);
    if ($[17] !== className) {
      t9 = cn("CartItem flex gap-comfy-sm", className);
      $[17] = className;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    if ($[19] !== hideThumbnail || $[20] !== item) {
      t10 = !hideThumbnail && <div className="CartItem__thumb"><CartItemImage item={item} /></div>;
      $[19] = hideThumbnail;
      $[20] = item;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    t8 = "CartItem__content items-center align-center";
    t6 = "CartItem__title flex justify-between label-lg";
    t7 = title;
    t5 = "CartItem__price pl-comfy-sm ml-auto label-lg label-amplified";
    T0 = Intl;
    t4 = "numbers.currencyCustom";
    t2 = item.commitmentFrequency;
    t3 = relatedAddOnInCart ? calcItemAmountWithAddOn(item, currentAddon(), relatedAddOnInCart).combinedAmount : item.amount;
    $[2] = cart;
    $[3] = className;
    $[4] = hideThumbnail;
    $[5] = item;
    $[6] = title;
    $[7] = T0;
    $[8] = t10;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    T0 = $[7];
    t10 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  let t11;
  if ($[22] !== t2 || $[23] !== t3) {
    t11 = {
      context: t2,
      amount: t3
    };
    $[22] = t2;
    $[23] = t3;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== T0 || $[26] !== t11 || $[27] !== t4) {
    t12 = <T0 k={t4} variables={t11} />;
    $[25] = T0;
    $[26] = t11;
    $[27] = t4;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12 || $[30] !== t5) {
    t13 = <span className={t5}>{t12}</span>;
    $[29] = t12;
    $[30] = t5;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13 || $[33] !== t6 || $[34] !== t7) {
    t14 = <div className={t6}>{t7}{t13}</div>;
    $[32] = t13;
    $[33] = t6;
    $[34] = t7;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== subtitle) {
    t15 = subtitle && <div className="CartItem__subtitle label-md mt-snug-lg">{subtitle}</div>;
    $[36] = subtitle;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== children || $[39] !== t14 || $[40] !== t15 || $[41] !== t8) {
    t16 = <div className={t8}>{t14}{t15}{children}</div>;
    $[38] = children;
    $[39] = t14;
    $[40] = t15;
    $[41] = t8;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t10 || $[44] !== t16 || $[45] !== t9) {
    t17 = <div className={t9}>{t10}{t16}</div>;
    $[43] = t10;
    $[44] = t16;
    $[45] = t9;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  return t17;
};